import {Chess, DEFAULT_POSITION} from "chess.js";

// Collects what Rook needs to know about a position, using the Stockfish that runs in the browser:
// the 3 best lines, what the opponent threatens, and how the position looked before the last move.
// `position` is {startFen, moves: [uci]}.

const cache = new Map();
const CACHE_SIZE = 40;

const cached = async (key, compute) => {
    if (cache.has(key)) return cache.get(key);
    const value = await compute();
    cache.set(key, value);
    if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
    return value;
};

const slim = (result) => ({
    lines: (result.lines || []).slice(0, 3).map((l) => ({cp: l.cp, mate: l.mate, pv: l.pv.slice(0, 10)}))
});

const flipTurn = (fen) => {
    const parts = fen.split(" ");
    parts[1] = parts[1] === "w" ? "b" : "w";
    parts[3] = "-";
    return parts.join(" ");
};

// Moves written in the question ("what about Bc4?", "why not g4", "and if he plays ...Qh4?"). Each one is tried for the
// side to move, and otherwise for the opponent (a "what if"). At most 3, so the answer doesn't take long.
const SAN = /(?<![\w])(O-O-O|O-O|[KQRBN][a-h]?[1-8]?x?[a-h][1-8]|[a-h]x[a-h][1-8](?:=[QRBN])?|[a-h][1-8](?:=[QRBN])?)[+#]?(?![\w])/g;

export const mentionedMoves = (fen, question) => {
    const found = [];
    const seen = new Set();
    for (const match of String(question || "").matchAll(SAN)) {
        const token = match[1];
        for (const opponent of [false, true]) {
            try {
                let start = fen;
                if (opponent) {
                    if (new Chess(fen).inCheck()) break;
                    start = flipTurn(fen);
                }
                const move = new Chess(start).move(token);
                const uci = move.from + move.to + (move.promotion || "");
                const key = `${opponent}|${uci}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    found.push({uci, opponent, fenAfter: (() => { const c = new Chess(start); c.move(token); return c.fen(); })()});
                }
                break;
            } catch (e) {
                // not a legal move for this side, try the other one
            }
        }
        if (found.length >= 3) break;
    }
    return found;
};

export const gatherRookContext = async (engine, position, {mode, playerColor, hintLevel, question} = {}) => {
    const startFen = position.startFen || DEFAULT_POSITION;
    const moves = position.moves || [];
    const chess = new Chess(startFen);
    moves.forEach((uci) => chess.move({from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4]}));
    const fen = chess.fen();
    const over = chess.isGameOver();

    const engineData = {lines: [], threat: null, before: null};
    if (engine && !over) {
        const now = await cached(`now|${startFen}|${moves.join(" ")}`,
            () => engine.analyse({startFen, moves}, {depth: 16, movetime: 900, multipv: 3}).then(slim));
        engineData.lines = now.lines;

        // What would the opponent do if they could move twice? Only when nobody is in check, because Stockfish
        // must never be given an impossible position.
        if (!chess.inCheck()) {
            const flipped = flipTurn(fen);
            const threat = await cached(`threat|${flipped}`,
                () => engine.analyse(flipped, {depth: 12, movetime: 400, multipv: 1}).then(slim)).catch(() => null);
            if (threat?.lines[0]) engineData.threat = threat.lines[0];
        }
    }
    // Moves the player asked about: what does Stockfish think happens after each one?
    if (engine && !over) {
        const mentioned = [];
        for (const m of mentionedMoves(fen, question)) {
            // A real move keeps the game history (repetitions); a "what if" for the opponent can only use the position
            const target = m.opponent ? m.fenAfter : {startFen, moves: [...moves, m.uci]};
            const after = await cached(`after|${m.opponent}|${m.fenAfter}|${moves.join(" ")}`,
                () => engine.analyse(target, {depth: 14, movetime: 600, multipv: 1}).then(slim)).catch(() => null);
            mentioned.push({move: m.uci, opponent: m.opponent, lines: after?.lines || []});
        }
        if (mentioned.length) engineData.mentioned = mentioned;
    }
    if (engine && moves.length) {
        const earlier = moves.slice(0, -1);
        const before = await cached(`now1|${startFen}|${earlier.join(" ")}`,
            () => engine.analyse({startFen, moves: earlier}, {depth: 15, movetime: 600, multipv: 1}).then(slim)).catch(() => null);
        if (before?.lines.length) engineData.before = before;
    }

    return {startFen, moves, mode, playerColor: playerColor || null, hintLevel: hintLevel || 0, engine: engineData};
};
